import React, { useEffect, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Lazy-load the game module so its Pixi.js bundle (~300KB gz) is fetched
// only when a player actually opens the Pet Runner page.
const PetRunnerCanvas = lazy(() => import('../../pet-runner/PetRunnerCanvas'));

/**
 * Hosts the Pet Runner game inside the main React tree. The Pixi application
 * is rendered into a <div> by PetRunnerCanvas and tears itself down on
 * unmount, so navigating away cleans up cleanly.
 */
export default function PetRunnerPlayer({ player, onLogout }) {
    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        if (!player?.name) navigate('/');
    }, [player, navigate]);

    return (
        <div className="min-h-screen flex flex-col bg-cyber-bg">
            <div className="neon-panel scanlines px-3 py-2 flex items-center justify-between gap-3">
                <button
                    type="button"
                    onClick={() => navigate('/games')}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-cyber-cyan/40 text-cyber-cyan text-[11px] font-display tracking-widest hover:bg-cyber-cyan/10 hover:border-cyber-cyan/70 transition"
                >
                    <span aria-hidden="true">←</span>
                    <span>{t('common.allGames', 'All games')}</span>
                </button>
                <div className="text-xs font-display tracking-widest text-cyber-dim">
                    {t('games.petRunner.title', 'Pet Runner')}
                </div>
                <div className="text-[10px] font-display tracking-widest text-cyber-dim">
                    {player?.name ? `// ${player.name}` : ''}
                </div>
                <span style={{ display: 'none' }} aria-hidden="true">
                    {typeof onLogout}
                </span>
            </div>

            <div className="flex-1 relative">
                <Suspense fallback={<div className="absolute inset-0 flex items-center justify-center text-cyber-dim tracking-widest text-xs">{t('common.loading', 'Loading...')}</div>}>
                    <PetRunnerCanvas />
                </Suspense>
            </div>
        </div>
    );
}
